import { Directive, ElementRef, HostListener, Input, Optional } from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
  selector: '[appCurrencyFormatter]'
})
export class CurrencyFormatterDirective {
  @Input() prefix: string = '';       // e.g. 'R'
  @Input() mixed: boolean = false;    // allow 'Mixed' value optionally

  private regex: RegExp = /^[0-9]+(\.[0-9]{0,2})?$/;

  constructor(private el: ElementRef, @Optional() private control: NgControl) {}

  ngOnInit() {
    this.formatInput(this.el.nativeElement.value); // Format initial value if any
  }

  @HostListener('input', ['$event.target.value'])
  onInput(value: string) {
    this.formatInput(value);
  }

  @HostListener('blur', ['$event.target.value'])
  onBlur(value: string) {
    this.formatInput(value, true); // Format with 2 decimal digits on blur
  }

  @HostListener('keypress', ['$event'])
  onKeyPress(event: KeyboardEvent) {
    const input = this.el.nativeElement as HTMLInputElement;
    const value = input.value.replace(/,/g, '').replace(/R/g, '');

    const isDecimal = event.key === '.';
    const hasDecimal = value.includes('.');
    const decimals = value.split('.')[1];
    const isTooLong = decimals && decimals.length >= 2;

    // Block non-numeric and multiple decimals
    if (!/[0-9.]/.test(event.key) || (isDecimal && hasDecimal)) {
      event.preventDefault();
    }

    // Block more than 2 digits after decimal
    if (hasDecimal && isTooLong && input.selectionStart! > value.indexOf('.')) {
      event.preventDefault();
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    event.preventDefault();

    const pastedData = event.clipboardData?.getData('text') || '';
    const cleaned = pastedData.replace(/[^0-9.]/g, '').slice(0, 15);

    const formatted = this.formatNumber(cleaned, false);
    this.el.nativeElement.value = formatted;
    this.control?.control?.setValue(formatted);
  }

  private formatInput(value: string, blur: boolean = false) {
    if (this.mixed && value === 'Mixed') {
      return;
    }

    if (!value) {
      this.setValue('');
      return;
    }

    const cleaned = value.replace(/,/g, '').replace(/R/g, '').trim();

    // If user enters ".", treat as "0.00"
    const normalized = cleaned === '.' ? '0.00' : cleaned.startsWith('.') ? `0${cleaned}` : cleaned;

    if (!this.regex.test(normalized)) {
      this.setValue('');
      return;
    }

    const formatted = this.formatNumber(normalized, blur);
    this.setValue(formatted);
  }

  private setValue(value: string) {
    this.el.nativeElement.value = value;
    this.control?.control?.setValue(value, { emitEvent: false });
  }

  private formatNumber(value: string, blur: boolean): string {
    let [integer, decimal] = value.split('.');
    integer = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    if (decimal) {
      decimal = decimal.slice(0, 2);
    }

    if (blur) {
      if (!decimal) decimal = '00';
      else if (decimal.length === 1) decimal += '0';
    }

    return `${this.prefix}${integer}${decimal !== undefined ? '.' + decimal : ''}`;
  }
}
